import React, { useState, useEffect, ReactNode, forwardRef, useContext } from "react";
import classnames from 'classnames';
import './TimePeriodFilters.scss';
import { DefaultConfigWizardContextValue, DefaultConfigWizardContext } from "../../DefaultsConfigWizard/DefaultsConfigWizardContext";
import { useUser } from "@/contexts/UserContext";
import { TimePeriodPicker } from "@/components/TimePeriodPicker";
import { RelativeTimePeriod, TimePeriod } from "@/types/ReportRun";
import { DefaultConfigLabels } from "@/pages/Onboarding/OnboardingSteps";
import { getTimePeriodType } from "@/utils/timeperiodUtil";

export type TimePeriodFiltersRef = {
    value?: DefaultConfigWizardContextValue['value'];
}

export type TimePeriodFiltersProps = {
    name?: string;
    onChange?: (e: ByzzerChangeEvent<TimePeriod>) => void;
    value?: TimePeriod;
    summary?: ReactNode;
    labelsAndTips?: DefaultConfigLabels;
 } & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

export const TimePeriodFilters = forwardRef<TimePeriodFiltersRef, TimePeriodFiltersProps>((
    {
        className,
        name,
        value,
        onChange,
        labelsAndTips,
        summary = labelsAndTips?.time_period_header,
        ...props
    }, ref) => {

    const baseClassName = 'byz-time-period-filters';

    const { value: contextValue, onChange: onContextChange, state: contextState, selectedDataset } = useContext(
        DefaultConfigWizardContext
    );
    
    const timePeriodType: RelativeTimePeriod['type'] = getTimePeriodType(selectedDataset);
    const [timePeriod, setTimePeriod] = useState<TimePeriod>({
        type: timePeriodType,
    });

    const { company, features: userFeatures = {}, defaultRunConfig } = useUser();

    function handleChange(e: ByzzerChangeEvent<unknown>) {
        onContextChange?.(e.name as keyof typeof defaultRunConfig, e.value, e.data);
    }

    // Initialize time period on mount based on dataset if context doesn't have it
    useEffect(() => {
        if (!contextValue?.timePeriod) {
            const defaultTimePeriod = { type: getTimePeriodType(selectedDataset) };
            setTimePeriod(defaultTimePeriod);
            onContextChange?.('timePeriod', defaultTimePeriod);
        }
    }, []); // Only run on mount

    useEffect(() => {
        setTimePeriod(contextValue?.timePeriod ?? value ?? {
            type: getTimePeriodType(selectedDataset),
        })
    }, [contextValue, value])
    
    return (
        <div className={classnames(baseClassName, className)} {...props}>
            {summary && <div className={`${baseClassName}__summary`}>{summary}</div>}
            <br/>
            <TimePeriodPicker
                name={'timePeriod'}
                value={timePeriod}
                onChange={handleChange}
                datatype={selectedDataset}
                hideLatestDate={true}
                hideCustomDate={selectedDataset === 'osh'} // Per UX, we are hiding custom date option for Omnishopper since it doesn't have historical data
            />
        </div>
    )
});

export default TimePeriodFilters;

TimePeriodFilters.displayName = 'TimePeriodFilters';